import { useQuery, useMutation } from '@tanstack/react-query';
import { validateSignatureToken, submitSignature } from '../api/signatureTokenService';
import { TokenStatus, ValidateTokenResponse } from '../../types/signatureToken';
import { toast } from 'react-toastify';

// ========================================
// QUERY HOOKS PARA SIGNATURE TOKENS
// ========================================

/**
 * Hook para validar token de firma desde la URL
 * 
 * Configuración:
 * - staleTime: 0 - No cachear, siempre validar
 * - retry: false - No reintentar, mostrar error directo
 * - refetchOnWindowFocus: false - No revalidar al enfocar ventana
 */
export const useValidateSignatureToken = (token?: string) => {
  return useQuery({
    queryKey: ['signature-token', 'validate', token],
    queryFn: () => validateSignatureToken(token!),
    enabled: !!token, // Solo ejecutar si hay token
    staleTime: 0, // No cachear, siempre validar
    retry: false, // No reintentar, mostrar error directo
    refetchOnWindowFocus: false, // No revalidar al enfocar ventana
  });
};

// ========================================
// MUTATION HOOKS PARA SIGNATURE TOKENS
// ========================================

/**
 * Hook para enviar firma de contrato
 * 
 * Configuración:
 * - No cachea resultados
 * - Muestra toast de éxito/error automáticamente
 */
export const useSubmitSignatureMutation = () => {
  return useMutation({
    mutationFn: ({ 
      token, 
      clientDni, 
      signatureDataURL 
    }: {
      token: string;
      clientDni: string;
      signatureDataURL: string;
    }) => submitSignature(token, clientDni, signatureDataURL),
    
    onSuccess: (data) => {
      if (data.success) {
        console.log('✅ Firma procesada exitosamente');
        // NO mostramos toast aquí, lo maneja el componente
      }
    },
    
    onError: (error: any) => {
      console.error('❌ Error en mutación de firma:', error);
      const message = error.response?.data?.message || 'Error al enviar la firma';
      toast.error(message);
    },
  });
};

// ========================================
// HELPER FUNCTIONS
// ========================================

/**
 * Helper para determinar el estado del token basándose en la respuesta
 * 
 * @param isLoading - Estado de carga de React Query
 * @param isError - Estado de error de React Query
 * @param data - Datos de respuesta del endpoint
 * @returns Estado del token para la UI
 */
export const getTokenStatus = (
    isLoading: boolean,
    isError: boolean,
    data?: ValidateTokenResponse
): TokenStatus => {
    // Estado de carga
    if (isLoading) return 'loading';
    
    // Error de red o servidor
    if (isError) return 'error';
    
    // Sin datos
    if (!data) return 'notfound';
    
    // Token inválido, expirado o usado
    if (data.success === false) {
        if (data.expired) return 'expired';
        if (data.used) return 'used';
        return 'notfound';
    }
    
    // Token válido
    if (data.success === true) return 'valid';
    
    // Fallback
    return 'error';
};
